import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Silhouette } from "@/components/Silhouette";
import { StatusPill, effectiveStatuses } from "@/lib/pipeline";
import { AreaBadge } from "@/components/db/areaStyle";
import { Loader2, MoreHorizontal, Archive, ArchiveRestore, EyeOff, Trash2, History, StickyNote, CheckSquare, Square } from "lucide-react";

export const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d.toLocaleDateString("sk-SK") : "—";
};
const skillNames = (c) => (c.skills || []).map((s) => (typeof s === "string" ? s : s.name)).filter(Boolean);
const candAreas = (c) => [...new Set([...(c.positions || []).map((x) => x.area), ...(c.areas || [])].filter(Boolean))];

export function Avatar({ c, size = "h-9 w-9" }) {
  return c.photo_url
    ? <img src={c.photo_url} alt={c.name} className={`${size} rounded-full object-cover flex-shrink-0`} />
    : <div className={`${size} rounded-full bg-slate-100 flex items-center justify-center text-slate-300 flex-shrink-0`} title="Bez fotografie"><Silhouette gender={c.gender} size={20} /></div>;
}

const GDPR = { ACTIVE: "aktívny", EXPIRING_SOON: "vyprší", EXPIRED: "expirovaný", REVOKED: "odvolaný", PENDING: "čaká" };

const COLS = "grid grid-cols-[28px_minmax(200px,1.6fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(140px,1.2fr)_96px_120px_44px] items-center gap-3";

export function CandidateTable({ rows, reload, openHistory, selected, onToggle, onToggleAll }) {
  const allSel = rows.length > 0 && rows.every((c) => selected.includes(c.id));
  return (
    <div className="overflow-x-auto rounded-3xl bg-white border border-slate-200 shadow-sm" data-testid="candidate-table">
      <div className="min-w-[1050px]">
        <div className={`${COLS} px-4 py-3 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wide text-slate-400`}>
          <button data-testid="select-all" onClick={onToggleAll} className="text-slate-400 hover:text-violet-700">{allSel ? <CheckSquare size={16} className="text-violet-600" /> : <Square size={16} />}</button>
          <div>Kandidát</div><div>Pozícia</div><div>Oblasť</div><div>Zručnosti</div><div>Dátum pridania</div><div>Stav</div><div className="text-center">Akcie</div>
        </div>
        {rows.map((c) => <Row key={c.id} c={c} reload={reload} openHistory={openHistory} selected={selected.includes(c.id)} onToggle={() => onToggle(c.id)} />)}
      </div>
    </div>
  );
}

function Row({ c, reload, openHistory, selected, onToggle }) {
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const act = async (fn, ok, confirmMsg) => {
    setMenu(false);
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try { await fn(); toast.success(ok); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const editNote = () => {
    setMenu(false);
    const text = window.prompt("Poznámka recruitera:", c.quick_note || "");
    if (text !== null) act(() => api.put(`/company/candidate/${c.id}/quick-note`, { text }), "Poznámka uložená");
  };
  const positions = (c.positions || []).map((x) => x.name);
  return (
    <div data-testid={`candidate-manage-row-${c.id}`}
      className={`${COLS} px-4 py-3 border-b border-slate-100 last:border-0 transition-colors ${selected ? "bg-violet-50/60" : "hover:bg-slate-50/70"} ${c.archived ? "opacity-75" : ""}`}>
      <button data-testid={`select-${c.id}`} onClick={onToggle} className="text-slate-400 hover:text-violet-700">{selected ? <CheckSquare size={16} className="text-violet-600" /> : <Square size={16} />}</button>
      <div className="flex items-center gap-3 min-w-0">
        <Avatar c={c} />
        <div className="min-w-0">
          <button data-testid={`candidate-name-${c.id}`} onClick={() => nav(`/company/candidate/${c.id}`)}
            className="block max-w-full truncate text-left text-sm font-semibold text-[#1e2a5a] hover:text-violet-700 hover:underline">{c.name}</button>
          <div className="truncate text-xs text-slate-500">{c.title}</div>
        </div>
        {typeof c.career_score === "number" && <span className="ml-auto flex-shrink-0 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700" title="Career Score">{c.career_score}</span>}
      </div>
      <div className="truncate text-xs text-slate-600" data-testid={`row-positions-${c.id}`}>{positions.join(", ") || "—"}</div>
      <div className="flex flex-wrap gap-1 min-w-0" data-testid={`row-areas-${c.id}`}>{candAreas(c).map((a) => <AreaBadge key={a} name={a} />)}{!candAreas(c).length && <span className="text-xs text-slate-300">—</span>}</div>
      <div className="truncate text-xs text-slate-500" data-testid={`row-skills-${c.id}`}>{skillNames(c).slice(0, 3).join(", ") || "—"}</div>
      <div className="text-xs text-slate-600" data-testid={`date-added-${c.id}`}>{fmtDate(c.created_at)}</div>
      <div className="flex flex-col items-start gap-1">
        {effectiveStatuses(c).map((st) => <StatusPill key={st} status={st} testId={`row-status-${c.id}-${st}`} />)}
        {GDPR[c.gdpr_status] && <span data-testid="gdpr-mini" className="text-[10px] text-slate-400">GDPR: {GDPR[c.gdpr_status]}</span>}
      </div>
      <div className="relative flex justify-center">
        <button data-testid={`manage-btn-${c.id}`} onClick={() => setMenu(!menu)} disabled={busy}
          className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <MoreHorizontal size={16} />}
        </button>
        {menu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
            <div data-testid={`manage-menu-${c.id}`} className="absolute right-0 top-9 z-50 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              {!c.anonymized && <Item testId={`row-note-${c.id}`} icon={StickyNote} onClick={editNote}>Poznámka recruitera</Item>}
              <Item testId={`history-btn-${c.id}`} icon={History} onClick={() => { setMenu(false); openHistory(c); }}>História komunikácie</Item>
              {c.archived
                ? <Item testId={`row-unarchive-${c.id}`} icon={ArchiveRestore} onClick={() => act(() => api.post(`/company/candidate/${c.id}/unarchive`), "Obnovené")}>Obnoviť z archívu</Item>
                : <Item testId={`row-archive-${c.id}`} icon={Archive} onClick={() => act(() => api.post(`/company/candidate/${c.id}/archive`), "Archivované")}>Archivovať</Item>}
              {!c.anonymized && <Item testId={`row-anonymize-${c.id}`} icon={EyeOff} danger onClick={() => act(() => api.post(`/company/candidate/${c.id}/anonymize`), "Osobné údaje anonymizované", "Naozaj natrvalo anonymizovať osobné údaje tohto kandidáta? Táto akcia je nevratná.")}>Anonymizovať</Item>}
              <Item testId={`row-delete-${c.id}`} icon={Trash2} danger onClick={() => act(() => api.delete(`/company/candidate/${c.id}`), "Profil natrvalo vymazaný", "Naozaj NATRVALO vymazať celý profil bez zanechania stopy? Vymažú sa všetky údaje, poznámky aj CV. Táto akcia je nevratná.")}>Vymazať celý profil</Item>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Item({ testId, icon: Icon, onClick, danger, children }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${danger ? "text-red-600 hover:bg-red-50" : "text-slate-700 hover:bg-slate-50"}`}>
      <Icon size={15} /> {children}
    </button>
  );
}
